import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Spacings, mediaQuery, Fonts } from '@styles/index';
import H5 from '@base/H5/H5';
import H3 from '@base/H3/H3';
import P2 from '@base/P2/P2';
import Image from '@base/Image';
import pushRoute from '@utils/pushRoute';
import Button from '@base/Button/Button';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { LOGIN_STARTED } from '@cont/Login/constants';
import { useToggleable } from '@stores/toggleables';
import { getIsLoggedIn } from '@api/selectors/auth';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { readCookie } from '@utils/cookies';
import CircularLoader from '@base/CircularLoader/CircularLoader';
import { CARD_TYPES, REFERRAL_HOME_PAGE_VIEWED } from '../constants';
import HeroCard from './HeroCard';
import AboutReferrals from './AboutReferral';

const ProgressContainer = styled.div`
  margin-top: ${Spacings.SPACING_50B};
  grid-column: span 10;
  justify-content: center;
  display: flex;
  height: 50vh;
`;

const Container = styled.div`
  margin-top: 70px;
  ${mediaQuery.aboveMobile} {
    background-color: var(--color-i-white);
  }
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_12B};
    .hero-reward-card {
      flex: 1;
    }
    .hero-reward-container {
      margin-right: ${Spacings.SPACING_4B};
      margin-top: -${Spacings.SPACING_1B};
    }
    .hero-wrapper {
      height: unset;
      padding-bottom: ${Spacings.SPACING_6B};
    }
  }
`;

const BlocksWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 1136px;
  margin: auto;
  margin-bottom: -${Spacings.SPACING_10B};
  ${mediaQuery.uptoTablet} {
    flex-direction: column;
    background-color: var(--color-i-white);
    width: 100%;
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
  }
`;

const OtherBlockWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: ${({ $isWon }) => ($isWon ? 'column-reverse' : 'column')};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_0B};
  }
`;

const RightBlock = styled.div``;

const ReferralBlock = styled.div`
  margin-top: ${Spacings.SPACING_10B};
  display: flex;
  flex-direction: row;
  ${mediaQuery.uptoMobile} {
    flex-direction: column;
    align-items: center;
    margin-top: ${Spacings.SPACING_5B};
  }
`;

const LoginSectionLeft = styled.div`
  position: sticky;
  width: 100%;
  .stats-wrapper {
    flex-direction: column;
    margin-top: ${Spacings.SPACING_4B};
  }
`;

const ReferralHeadingWrapper = styled.div`
  margin-top: ${Spacings.SPACING_10B};
  margin-bottom: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_5B};
    margin-bottom: ${Spacings.SPACING_2B};
  }
`;

const ReferralHeading = styled(H3)`
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H4};
  }
`;

const LoginReward = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${Spacings.SPACING_6B};
  padding: ${Spacings.SPACING_5B} ${Spacings.SPACING_4B};
  gap: ${Spacings.SPACING_4B};
  border: 1px solid var(--color-divider);
  border-radius: ${Spacings.SPACING_2B};
`;

const PointText = styled(H5)`
  flex-grow: 1;
`;

const BottomCard = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid var(--color-divider);
  position: fixed;
  background: var(--color-i-white);
  padding: ${Spacings.SPACING_4B};
  bottom: 0;
  z-index: 1;
  ${mediaQuery.aboveMobile} {
    display: none;
  }
`;

const ReferralCodeWrapper = styled.div`
  text-align: left;
`;

const ReferralCode = styled.span`
  margin-top: ${Spacings.SPACING_1B};
  font-size: ${Fonts.H4};
  font-weight: ${Fonts.HEAVY};
  color: var(--color-text-secondary);
  line-height: 26px;
`;

const ActionButtonContainer = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_2B};
`;

const ReferButton = styled(Button)`
  font-size: ${Fonts.SMALL};
  width: 113px;
`;

const FreeReferral = ({ feedData, rewardDetails }) => {
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const selectedGoalUID = useSelectedGoalUID();
  const goalSegmentData = useGoalEventProps({ goalUID: selectedGoalUID });
  const accessToken = readCookie('accessToken');
  const [loader, showLoader] = useState(false);

  let heroCardData = null;
  let referralBenefits = null;

  useEffect(() => {
    if (accessToken) showLoader(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    segment.track(REFERRAL_HOME_PAGE_VIEWED, {
      ...goalSegmentData
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  feedData?.forEach((feed) => {
    switch (feed.type) {
      case CARD_TYPES.HERO_CARD:
        [heroCardData] = feed.data;
        heroCardData.extraBlockInfo = feed.extraBlockInfo;
        break;
      case CARD_TYPES.REFERRAL_BENEFITS:
        [referralBenefits] = feed.data;
        break;
      default:
        break;
    }
  });

  useEffect(() => {
    if (isLoggedIn || accessToken) {
      pushRoute('/settings/referral', '/settings/referral');
    }
  }, [isLoggedIn, accessToken]);

  const handleReferNow = () => {
    openLoginDrawer();
    segment.track(LOGIN_STARTED, {
      ...goalSegmentData,
      last_primary_source_section: 'Refer Now'
    });
  };

  if (loader) {
    return (
      <ProgressContainer>
        <CircularLoader />
      </ProgressContainer>
    );
  }

  return (
    <Container>
      <HeroCard
        heroCardData={heroCardData}
        buttonText="Refer now"
        referNow={handleReferNow}
        rewardDetails={rewardDetails}
      />
      <BlocksWrapper>
        <OtherBlockWrapper>
          <ReferralBlock>
            <LoginSectionLeft>
              <ReferralHeadingWrapper>
                <ReferralHeading>Your rewards</ReferralHeading>
                <LoginReward>
                  <Image
                    src="referrals/lock.svg"
                    width={40}
                    height={40}
                    alt="avatar"
                  />
                  <PointText>
                    Login to view the rewards you’ve won so far
                  </PointText>
                </LoginReward>
              </ReferralHeadingWrapper>
            </LoginSectionLeft>
          </ReferralBlock>
        </OtherBlockWrapper>
        <RightBlock>
          <AboutReferrals
            rewardDetails={rewardDetails}
            referralBenefits={referralBenefits}
          />
        </RightBlock>
      </BlocksWrapper>
      <BottomCard>
        <ReferralCodeWrapper>
          <P2>Login to view your referral code</P2>
          <ReferralCode>XXXXXXXX</ReferralCode>
        </ReferralCodeWrapper>
        <ActionButtonContainer>
          <ReferButton
            label="Refer now"
            color="black"
            grid={3}
            onClick={handleReferNow}
          />
        </ActionButtonContainer>
      </BottomCard>
    </Container>
  );
};

export default FreeReferral;
